// CRM 2.0 · M12-F3 Step 2: Templates › Booking drafts — the test page for the draft engine until the CRM 2.0 cards
// call it (KYC submit makes the review draft, Generate Final Draft on the Agreement Preparation card the final draft,
// Print & Sign the printed copy). Pick a CRM 2.0 booking, see which template it gets, make a review / final draft or
// a printed copy (Step 3), see every version and copy, download. Sandbox only: an old-flow booking with Agreement KYC
// can be opened by its id; its documents are marked TEST.
// Top sheet (DC-224 / DC-225): each final draft version and printed copy gets its own top sheet, listed under it;
// "Print top sheet again" makes a fresh one; a printed copy's "Print PDF" is its top sheet as page 1 + the agreement.
import React, { useEffect, useRef, useState } from "react";
import cls from "./Templates.module.css";
import { fmtDate } from "./templateCommon";
import { listBookingDrafts, getBookingDrafts, getBookingDraftsMode, makeBookingDraft, getBookingDocumentLink, handCopyToSignatory, listSignatories, printTopSheetAgain, getReissueInfo } from "../../Services/Templates";
import { errorTost, successTost, infoTost } from "../../Services/Toast";
import { presentLoader, dismissLoader } from "../../Services/loader";

const KIND = { REVIEW_DRAFT: "Review draft", FINAL_DRAFT: "Final draft", FINAL_COPY: "Printed copy", TOP_SHEET: "Top sheet", ALLOTMENT_LETTER: "Allotment letter" };
const STATUS = { READY: ["Current", cls.cOk], SENT: ["Sent", cls.cOk], CONFIRMED: ["Confirmed", cls.cOk], REPLACED: ["Replaced", cls.cGrey], CANCELLED: ["Cancelled", cls.cGrey], FAILED: ["Failed", cls.cErr] };
const numberOf = (d) => (d.documentTypeKey === "ALLOTMENT_LETTER" ? d.allotmentNo || (d.status === "FAILED" ? `Try ${d.tryNo || ""}`.trim() : "—")
  : `${{ REVIEW_DRAFT: "Revision", FINAL_DRAFT: "Version", FINAL_COPY: "Copy", TOP_SHEET: "No." }[d.documentTypeKey] || "Version"} ${d.versionNo}`);
const CURRENT = ["READY", "SENT", "CONFIRMED"];
// each top sheet right under the final draft / printed copy it belongs to
function inOrder(documents) {
  const tops = documents.filter((d) => d.documentTypeKey === "TOP_SHEET");
  return documents.filter((d) => d.documentTypeKey !== "TOP_SHEET")
    .flatMap((d) => [d, ...tops.filter((t) => t.attachedToDocumentId === d.crm2BookingDocumentId)]);
}

// "Print top sheet again": a fresh top sheet (today's status values) for the same final draft version / copy
function TopSheetAgainModal({ doc, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Print top sheet again">
      <div className={cls.modal} style={{ maxWidth: "52rem" }}>
        <div className={cls.modalHead}>
          <h2>Print top sheet again · {doc.documentTypeKey === "FINAL_COPY" ? `Copy ${doc.versionNo}` : `Final draft version ${doc.versionNo}`}</h2>
          <p className={cls.hint}>
            A new top sheet with today's status values{doc.documentTypeKey === "FINAL_COPY" ? ", the same copy no. and copy QR" : ""}. The earlier top sheet is kept as Replaced.
          </p>
        </div>
        <div className={cls.modalBody}>
          <div>
            <label className={cls.lbl} htmlFor="tsReason">Reason<sup>*</sup></label>
            <input id="tsReason" className={cls.inp} value={reason} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="e.g. Appointment booked since" />
          </div>
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} onClick={go} disabled={busy || !reason.trim()}>{busy ? "Printing…" : "Print top sheet"}</button>
        </div>
      </div>
    </div>
  );
}
const COPY_STATUS = { READY_TO_PRINT: ["Ready to print", cls.cOk], WITH_SIGNATORY: ["With signatory", cls.cLt], SENT_BACK: ["Sent back", cls.cErr], CANCELLED: ["Cancelled", cls.cGrey] };
const pageOf = (p) => (p.tracking ? (p.topSheet ? "Top sheet" : "Page 1 · Copy QR") : `Page ${p.pageNo}`);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const testChip = <span className={`${cls.chip} ${cls.cWarn}`} style={{ marginLeft: ".6rem" }}>TEST</span>;
const nextCopyNo = (copies) => copies.reduce((m, c) => Math.max(m, c.copyNo), 0) + 1;

// copy 2 onwards: the earlier copies are cancelled, so a reason is needed (it shows on the scan notice)
function PrintCopyModal({ copies, initialReason, onCancel, onConfirm }) {
  const [reason, setReason] = useState(initialReason || "");
  const [busy, setBusy] = useState(false);
  const next = nextCopyNo(copies);
  async function go() {
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim() || null });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Make printed copy">
      <div className={cls.modal} style={{ maxWidth: "56rem" }}>
        <div className={cls.modalHead}>
          <h2>{next === 1 ? "Make the printed copy" : `Make printed copy ${next}`}</h2>
          <p className={cls.hint}>
            The current final draft again, with a QR on every page with booking data, and its top sheet with this copy's QR (page 1 of the print PDF). Every field must
            still have the final draft's value, except the print-time fields (Templates › Settings › Booking drafts).
          </p>
        </div>
        <div className={cls.modalBody}>
          {copies.length > 0 && (
            <div className={cls.blockBox} style={{ background: "var(--warn-bg)", borderColor: "#FEDF89", color: "var(--warn)" }}>
              {copies.length} {copies.length === 1 ? "copy" : "copies"} printed before:{" "}
              {copies.map((c) => `Copy ${c.copyNo} on ${fmtDate(c.printedAt)}${c.printedByName ? ` by ${c.printedByName}` : ""}`).join("; ")}.
              {" "}They are cancelled and their QR codes stop counting.
            </div>
          )}
          {copies.length > 0 && (
            <div>
              <label className={cls.lbl} htmlFor="pcReason">Reason<sup>*</sup></label>
              <input id="pcReason" className={cls.inp} value={reason} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="e.g. Page 3 torn at signing" />
            </div>
          )}
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} onClick={go} disabled={busy || (copies.length > 0 && !reason.trim())}>
            {busy ? "Making the printed copy…" : next === 1 ? "Make printed copy" : `Make copy ${next}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reissue allotment letter (DC-237): the warning first, a reason; nothing is made before "Reissue letter"
function ReissueLetterModal({ info, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await onConfirm({ reason, note: note.trim() || null });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Reissue allotment letter">
      <div className={cls.modal} style={{ maxWidth: "56rem" }}>
        <div className={cls.modalHead}>
          <h2>Reissue allotment letter</h2>
          <p className={cls.hint}>A new letter with the next Allotment No., made from the Live template with every check of the first letter.</p>
        </div>
        <div className={cls.modalBody}>
          <div className={cls.blockBox} style={{ background: "var(--warn-bg)", borderColor: "#FEDF89", color: "var(--warn)" }}>
            {info.warning.map((w) => <p key={w} style={{ margin: ".2rem 0" }}>{w}</p>)}
          </div>
          <div>
            <label className={cls.lbl} htmlFor="rlReason">Reason<sup>*</sup></label>
            <select id="rlReason" className={cls.sel} value={reason} autoFocus onChange={(e) => setReason(e.target.value)}>
              <option value="">Pick the reason</option>
              {info.reasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className={cls.lbl} htmlFor="rlNote">Note (optional)</label>
            <input id="rlNote" className={cls.inp} value={note} maxLength={info.noteMax} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Co-applicant name corrected" />
          </div>
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} onClick={go} disabled={busy || !reason}>{busy ? "Reissuing the letter…" : "Reissue letter"}</button>
        </div>
      </div>
    </div>
  );
}

function FinalDraftModal({ hasCurrent, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  const needReason = hasCurrent && !reason.trim();
  async function go() {
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim() || null, customerVisible: visible });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className={cls.mask} role="dialog" aria-modal="true" aria-label="Make final draft">
      <div className={cls.modal} style={{ maxWidth: "56rem" }}>
        <div className={cls.modalHead}>
          <h2>{hasCurrent ? "Make a new final draft version" : "Make the final draft"}</h2>
          <p className={cls.hint}>
            Made from the template linked to this tower and plan, with parking. A value missing on the booking stops it and is named.
            {hasCurrent ? " The current version is kept as Replaced and its OTP no longer counts." : ""}
          </p>
        </div>
        <div className={cls.modalBody}>
          <div>
            <label className={cls.lbl} htmlFor="fdReason">Reason{hasCurrent ? <sup>*</sup> : " (optional)"}</label>
            <input id="fdReason" className={cls.inp} value={reason} autoFocus onChange={(e) => setReason(e.target.value)}
              placeholder={hasCurrent ? "e.g. Wrong carpet area in version 1" : ""} />
          </div>
          <label style={{ display: "flex", gap: ".8rem", alignItems: "center", fontSize: "1.4rem" }}>
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
            Customer can see it on the portal (Customer Preview)
          </label>
        </div>
        <div className={cls.modalFoot}>
          <button className={cls.btn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button className={`${cls.btn} ${cls.primary}`} onClick={go} disabled={busy || needReason}>{busy ? "Making the final draft…" : "Make final draft"}</button>
        </div>
      </div>
    </div>
  );
}

function BookingDrafts() {
  const [bookings, setBookings] = useState(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null); // allotmentPaymentId
  const [detail, setDetail] = useState(null);
  const [making, setMaking] = useState(null); // "FINAL" / "COPY" while that pop-up is open
  const [again, setAgain] = useState(null); // the final draft / copy whose top sheet is printed again
  const [copyReason, setCopyReason] = useState(""); // Print new copy of a sent-back copy: the reason filled in
  const [testAny, setTestAny] = useState(false); // sandbox: an old-flow booking can be opened by its id
  const [byId, setById] = useState("");
  const [signatories, setSignatories] = useState([]); // sandbox test: who a printed copy can be handed to
  const [signatoryId, setSignatoryId] = useState("");
  const [reissue, setReissue] = useState(null); // the Reissue allotment letter pop-up's facts while it is open
  const detailRef = useRef(null);

  useEffect(() => {
    let live = true;
    listBookingDrafts().then((list) => live && setBookings(list || [])).catch((e) => errorTost(e.message));
    getBookingDraftsMode().then((m) => {
      if (!live || !(m && m.testAnyBooking)) return;
      setTestAny(true);
      listSignatories().then((list) => live && setSignatories(list || [])).catch(() => {});
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  async function open(id) {
    setPicked(id);
    setDetail(null);
    presentLoader();
    try {
      setDetail(await getBookingDrafts(id));
      setTimeout(() => detailRef.current && detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }

  async function make(documentTypeKey, extra = {}) {
    presentLoader();
    try {
      const r = await makeBookingDraft(picked, { documentTypeKey, ...extra });
      if (documentTypeKey === "FINAL_COPY") successTost(`Printed copy ${r.copyNo} made (${r.pageCount} pages, ${r.pageQrs} page QRs, ${r.template})`);
      else if (documentTypeKey === "ALLOTMENT_LETTER" && extra.reissue) {
        if (r.existed) infoTost("This letter was already reissued: no second one made");
        else successTost(`Allotment letter ${r.allotmentNo || ""} reissued: the customer must sign it again with Aadhaar eSign`);
      }
      else if (documentTypeKey === "ALLOTMENT_LETTER") {
        if (r.existed) infoTost("The allotment letter is already made: it is never made again");
        else successTost(`Allotment letter ${r.allotmentNo || ""} made (${r.pageCount} pages, ${r.template}${r.signatory ? `, signed by ${r.signatory.name}` : ""})`);
      }
      else if (r.existed) infoTost(`Nothing changed since ${KIND[documentTypeKey].toLowerCase()} ${r.versionNo}: no new one made`);
      else successTost(`${KIND[documentTypeKey]} ${documentTypeKey === "REVIEW_DRAFT" ? "revision" : "version"} ${r.versionNo} made (${r.pageCount} pages, ${r.template})`);
      if (r.topSheetProblem) errorTost(r.topSheetProblem);
      ((r.topSheet && r.topSheet.warnings) || []).forEach((w) => infoTost(`Top sheet: ${w}`));
      setMaking(null);
      setReissue(null);
      setDetail(await getBookingDrafts(picked));
    } catch (e) {
      errorTost(e.message);
      // a failed allotment letter try is kept as a row: show it
      if (documentTypeKey === "ALLOTMENT_LETTER") setDetail(await getBookingDrafts(picked).catch(() => detail));
      const changed = e.details && e.details.changedFields;
      if (changed && changed.length) infoTost(`Changed: ${changed.join(", ")}`);
    } finally {
      dismissLoader();
    }
  }

  // sandbox test until the Document Desk step (M14-F3): the copy goes to the picked VJ Signatory
  async function openReissue() {
    try {
      const info = await getReissueInfo(picked);
      if (!info.allowed) errorTost("You cannot reissue the allotment letter: ask the CRM Head or Legal");
      else setReissue(info);
    } catch (e) {
      errorTost(e.message);
    }
  }

  async function handToSignatory(c) {
    const to = signatories.find((u) => u.userId === signatoryId) || signatories[0];
    if (!to) return;
    presentLoader();
    try {
      await handCopyToSignatory(c.crm2AgreementCopyId, to.userId);
      successTost(`Copy ${c.copyNo} is with ${to.name}: Agreement Sign`);
      setDetail(await getBookingDrafts(picked));
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }

  async function topSheetAgain(doc, reason) {
    presentLoader();
    try {
      const r = await printTopSheetAgain(doc.crm2BookingDocumentId, reason);
      successTost(`Top sheet ${r.versionNo} made`);
      setAgain(null);
      setDetail(await getBookingDrafts(picked));
    } catch (e) {
      errorTost(e.message);
    } finally {
      dismissLoader();
    }
  }

  async function download(d, print) {
    try {
      const { url } = await getBookingDocumentLink(d.crm2BookingDocumentId, print);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      errorTost(e.message);
    }
  }

  const q = search.trim().toLowerCase();
  const shown = (bookings || []).filter((b) => !q || [b.project, b.tower, b.unitNo].some((v) => String(v || "").toLowerCase().includes(q)));
  const b = detail && detail.booking;
  const current = (kind) => detail && detail.documents.find((d) => d.documentTypeKey === kind && ["READY", "SENT", "CONFIRMED"].includes(d.status));
  const copies = (detail && detail.copies) || [];

  return (
    <>
      <div className={cls.blockBox} style={{ background: "var(--warn-bg)", borderColor: "#FEDF89", color: "var(--warn)", marginBottom: "2rem" }}>
        Test page for the draft engine. In the real flow the review draft is made when the customer submits the Agreement KYC, and the
        final draft from Generate Final Draft on the Agreement Preparation card. Drafts made here are saved on the booking.
      </div>

      {testAny && (
        <div className={cls.actionbar}>
          <p className={cls.count}>Sandbox test: open an old-flow booking with Agreement KYC by its booking id (its documents are marked TEST)</p>
          <div className={cls.filters}>
            <input className={cls.inp} style={{ width: "36rem" }} placeholder="Booking id (allotmentPaymentId)" value={byId}
              onChange={(e) => setById(e.target.value.trim())} aria-label="Booking id" />
            <button className={cls.btn} disabled={!UUID.test(byId)} onClick={() => open(byId)}>Open</button>
          </div>
        </div>
      )}
      <div className={cls.actionbar}>
        <p className={cls.count}>{bookings ? `${shown.length} CRM 2.0 ${shown.length === 1 ? "booking" : "bookings"}` : "Loading…"}</p>
        <div className={cls.filters}>
          <input className={cls.inp} style={{ width: "26rem" }} placeholder="Search project, tower or unit" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search bookings" />
        </div>
      </div>
      <div className={cls.tblw}>
        <table className={cls.tbl}>
          <thead><tr><th>Project</th><th>Tower</th><th>Unit</th><th>Final Review</th><th className={cls.last} /></tr></thead>
          <tbody>
            {shown.map((x) => (
              <tr key={x.allotmentPaymentId}>
                <td className={cls.main}>{x.project}</td>
                <td>{x.tower}</td>
                <td>{x.unitNo}</td>
                <td>{x.locked ? <span className={`${cls.chip} ${cls.cGrey}`}>Approved</span> : <span className={`${cls.chip} ${cls.cLt}`}>Not yet</span>}</td>
                <td className={cls.last}><button className={cls.pillEdit} onClick={() => open(x.allotmentPaymentId)}>{picked === x.allotmentPaymentId ? "Opened" : "Open"}</button></td>
              </tr>
            ))}
            {bookings && !shown.length && <tr><td colSpan={5} className={cls.empty}>No CRM 2.0 bookings</td></tr>}
          </tbody>
        </table>
      </div>

      {b && (
        <div className={cls.panel} ref={detailRef} style={{ marginTop: "2.4rem" }}>
          <h2 className={cls.panelTitle}>{[b.project, b.tower, b.unitNo].filter(Boolean).join(" · ")}{!b.isCrm2 && testChip}</h2>
          {!b.isCrm2 && <p className={cls.hint} style={{ marginTop: 0, color: "var(--warn)" }}>Old-flow booking, sandbox test only: every document made here is marked TEST.</p>}
          <p className={cls.hint} style={{ marginTop: 0 }}>
            {b.applicants} {b.applicants === 1 ? "applicant" : "applicants"} · payment plan {b.paymentPlanId || "—"} · Final Review {b.locked ? "approved" : "not approved yet"}
          </p>
          {detail.template
            ? <div className={cls.okBox}>Template for this tower and plan: <b>{detail.template.label}</b></div>
            : <div className={cls.blockBox}>{detail.templateProblem}</div>}
          {b.applicants === 0 && <p className={cls.hint} style={{ color: "var(--warn)" }}>No Agreement KYC yet: the buyer part prints empty, and a final draft stops.</p>}
          {b.lookupWarnings.map((w) => <p key={w} className={cls.hint} style={{ color: "var(--warn)" }}>{w}</p>)}

          <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", margin: "1.6rem 0 2.4rem" }}>
            <span title={b.locked ? "Final Review is approved: the review draft cannot change any more" : undefined}>
              <button className={cls.btn} disabled={!detail.template || b.locked} onClick={() => make("REVIEW_DRAFT")}>
                {current("REVIEW_DRAFT") ? "Make review draft again" : "Make review draft"}
              </button>
            </span>
            <button className={`${cls.btn} ${cls.primary}`} disabled={!detail.template} onClick={() => setMaking("FINAL")}>
              {current("FINAL_DRAFT") ? "Make new final draft version" : "Make final draft"}
            </button>
            <span title={current("FINAL_DRAFT") ? undefined : "Make the final draft first"}>
              <button className={cls.btn} disabled={!current("FINAL_DRAFT")} onClick={() => setMaking("COPY")}>
                {copies.length ? `Make printed copy ${nextCopyNo(copies)}` : "Make printed copy"}
              </button>
            </span>
            <span title={current("ALLOTMENT_LETTER") ? "The allotment letter is made once: use Reissue allotment letter to replace it" : detail.letter && !detail.letter.ok ? detail.letter.reason : undefined}>
              <button className={cls.btn} disabled={!!current("ALLOTMENT_LETTER") || !(detail.letter && detail.letter.ok)} onClick={() => make("ALLOTMENT_LETTER")}>
                {current("ALLOTMENT_LETTER") ? "Allotment letter made" : "Make allotment letter"}
              </button>
            </span>
            {current("ALLOTMENT_LETTER") && <button className={cls.btn} onClick={openReissue}>Reissue allotment letter</button>}
          </div>
          {detail.letter && !detail.letter.ok && !current("ALLOTMENT_LETTER") && (
            <p className={cls.hint} style={{ marginTop: "-1.2rem", color: "var(--warn)" }}>Allotment letter can't be made: {detail.letter.reason}. Ask Legal.</p>
          )}

          <div className={cls.tblw}>
            <table className={cls.tbl}>
              <thead><tr><th>Document</th><th>Number</th><th>Status</th><th>Template</th><th>Pages</th><th>Made by</th><th>Made on</th><th>Reason</th><th className={cls.last} /></tr></thead>
              <tbody>
                {inOrder(detail.documents).map((d) => (
                  <tr key={d.crm2BookingDocumentId}>
                    <td className={`${cls.main} ${cls.nowrap}`} style={d.documentTypeKey === "TOP_SHEET" ? { paddingLeft: "3.2rem", fontWeight: 400 } : undefined}>
                      {d.documentTypeKey === "TOP_SHEET" ? "↳ " : ""}{KIND[d.documentTypeKey]}{d.test && testChip}
                    </td>
                    <td className={cls.nowrap}>{numberOf(d)}</td>
                    <td><span className={`${cls.chip} ${(STATUS[d.status] || [])[1] || ""}`}>{(STATUS[d.status] || [d.status])[0]}</span></td>
                    <td>{d.template}</td>
                    <td>{d.pageCount}</td>
                    <td className={cls.nowrap}>{d.madeByName || "—"}</td>
                    <td className={cls.nowrap}>{fmtDate(d.madeAt)}</td>
                    <td>
                      {d.reason || ""}
                      {d.documentTypeKey === "FINAL_DRAFT" && !d.customerVisible ? <div className={cls.hint}>Hidden from the customer</div> : null}
                      {d.documentTypeKey === "FINAL_COPY" ? (
                        <div className={cls.hint}>
                          From final draft version {d.finalDraftVersionNo} · {d.pageQrs} page QRs · top sheet: "{d.pageNote}"
                          {d.printTimeChanged.length ? ` · print-time fields changed: ${d.printTimeChanged.join(", ")}` : ""}
                        </div>
                      ) : null}
                      {d.documentTypeKey === "TOP_SHEET" ? <div className={cls.hint}>Internal · never shown to the customer{d.copyQr ? " · with the copy QR" : " · not for signing"}</div> : null}
                    </td>
                    <td className={cls.last} style={{ whiteSpace: "nowrap" }}>
                      {d.documentTypeKey === "FINAL_COPY" && CURRENT.includes(d.status) && (
                        <button className={cls.pillEdit} style={{ marginRight: ".6rem" }} onClick={() => download(d, true)} title="The top sheet as page 1, then the agreement">Print PDF</button>
                      )}
                      {["FINAL_DRAFT", "FINAL_COPY"].includes(d.documentTypeKey) && CURRENT.includes(d.status) && (
                        <button className={cls.pillEdit} style={{ marginRight: ".6rem" }} onClick={() => setAgain(d)}>Print top sheet again</button>
                      )}
                      <button className={cls.pillEdit} onClick={() => download(d)}
                        title={["REPLACED", "CANCELLED"].includes(d.status) ? "Downloads with REPLACED – do not use on every page" : undefined}>Download</button>
                    </td>
                  </tr>
                ))}
                {!detail.documents.length && <tr><td colSpan={9} className={cls.empty}>No drafts made for this booking yet</td></tr>}
              </tbody>
            </table>
          </div>

          <h3 className={cls.panelTitle} style={{ marginTop: "2.4rem", fontSize: "1.6rem" }}>Printed copies</h3>
          <div className={cls.tblw}>
            <table className={cls.tbl}>
              <thead><tr><th>Copy</th><th>Status</th><th>Printed</th><th>Cancelled</th><th>Reason</th><th>Page codes</th><th>Signatory</th></tr></thead>
              <tbody>
                {copies.map((c) => (
                  <tr key={c.crm2AgreementCopyId}>
                    <td className={`${cls.main} ${cls.nowrap}`}>Copy {c.copyNo}{c.test && testChip}</td>
                    {/* the status chip stays whole; date and name go on two lines so the table fits without scrolling sideways */}
                    <td className={cls.nowrap}><span className={`${cls.chip} ${(COPY_STATUS[c.status] || [])[1] || cls.cLt}`}>{(COPY_STATUS[c.status] || [c.status])[0]}</span></td>
                    <td>
                      <div className={cls.nowrap}>{fmtDate(c.printedAt)}</div>
                      {c.printedByName && <div className={cls.hint}>{c.printedByName}</div>}
                    </td>
                    <td>
                      {c.cancelledAt && <div className={cls.nowrap}>{fmtDate(c.cancelledAt)}</div>}
                      {c.cancelledAt && c.cancelledByName && <div className={cls.hint}>{c.cancelledByName}</div>}
                    </td>
                    <td>{c.cancelReason || ""}{c.replacedByCopyNo ? <div className={cls.hint}>Current copy: Copy {c.replacedByCopyNo}</div> : null}</td>
                    <td>
                      <div className={cls.nowrap}>{c.pageCodes.current} current</div>
                      <div className={`${cls.hint} ${cls.nowrap}`}>{c.pageCodes.replaced} replaced</div>
                    </td>
                    <td className={c.status === "SENT_BACK" ? undefined : cls.nowrap}>
                      {c.status === "SENT_BACK" ? (
                        // sent back by the signatory: the pages and notes; the Desk's only action is a new copy
                        <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", minWidth: "28rem" }}>
                          <b>Sent back by the signatory{c.statusChangedAt ? ` · ${fmtDate(c.statusChangedAt)}` : ""}</b>
                          {(c.signatoryCheck.notMatchingPages || []).map((p) => (
                            <div key={`${p.pageNo}-${p.tracking ? "t" : "p"}`} className={cls.hint} style={{ margin: 0, color: "var(--err)" }}>
                              <b>{pageOf(p)}:</b> {p.note || "—"}
                            </div>
                          ))}
                          <div>
                            <button className={cls.pillEdit} disabled={!current("FINAL_DRAFT")}
                              onClick={() => { setCopyReason(`Sent back by the signatory: ${(c.signatoryCheck.notMatchingPages || []).map((p) => `${pageOf(p)} ${p.note || ""}`.trim()).join("; ")}`); setMaking("COPY"); }}>
                              Print new copy
                            </button>
                          </div>
                        </div>
                      ) : !c.signatoryCheck.total ? "—" : c.signatoryCheck.done ? "Checked by signatory" : `${c.signatoryCheck.checked} of ${c.signatoryCheck.total} checked`}
                      {c.status !== "SENT_BACK" && c.signatoryCheck.notMatching ? <div className={cls.hint}>{c.signatoryCheck.notMatching} not matching</div> : null}
                      {testAny && ["READY_TO_PRINT", "PRINTED"].includes(c.status) && (
                        <div style={{ display: "flex", gap: ".6rem", marginTop: ".4rem", alignItems: "center" }}>
                          <select className={cls.sel} style={{ width: "18rem" }} value={signatoryId || (signatories[0] && signatories[0].userId) || ""}
                            onChange={(e) => setSignatoryId(e.target.value)} aria-label="Signatory">
                            {signatories.map((u) => <option key={u.userId} value={u.userId}>{u.name}</option>)}
                          </select>
                          <button className={cls.pillEdit} disabled={!signatories.length} onClick={() => handToSignatory(c)}>Hand to signatory (test)</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!copies.length && <tr><td colSpan={7} className={cls.empty}>No printed copy yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reissue && <ReissueLetterModal info={reissue} onCancel={() => setReissue(null)} onConfirm={(r) => make("ALLOTMENT_LETTER", { reissue: r })} />}
      {again && <TopSheetAgainModal doc={again} onCancel={() => setAgain(null)} onConfirm={(reason) => topSheetAgain(again, reason)} />}
      {making === "COPY" && (
        <PrintCopyModal copies={copies} initialReason={copyReason} onCancel={() => { setMaking(null); setCopyReason(""); }}
          onConfirm={(extra) => make("FINAL_COPY", extra).finally(() => setCopyReason(""))} />
      )}
      {making === "FINAL" && (
        <FinalDraftModal hasCurrent={!!current("FINAL_DRAFT")} onCancel={() => setMaking(null)}
          onConfirm={(extra) => make("FINAL_DRAFT", extra)} />
      )}
    </>
  );
}

export default BookingDrafts;
